import React, { useState, useEffect } from 'react';
import { api } from '../../hooks/useApi';
import { FEATURES } from '../../lib/features';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { SlidersHorizontal, Loader2 } from 'lucide-react';

const HOURS = Array.from({ length: 24 }, (_, h) => h);
const hourLabel = (h) => `${((h + 11) % 12) + 1}:00 ${h < 12 ? 'AM' : 'PM'}`;
const TIMEZONES = [
  'America/Edmonton', 'America/Vancouver', 'America/Regina', 'America/Winnipeg', 'America/Toronto',
  'America/Tijuana', 'America/Mazatlan', 'America/Mexico_City', 'UTC',
];

// Super admin: what one property shows and how its sales team works.
const PropertyFeaturesModal = ({ property, onClose, onSaved }) => {
  const [features, setFeatures] = useState({});
  const [label, setLabel] = useState('Advertisers');
  const [visibility, setVisibility] = useState('all');
  const [digest, setDigest] = useState(false);
  const [hour, setHour] = useState(7);
  const [timezone, setTimezone] = useState('America/Edmonton');
  const [multiCurrency, setMultiCurrency] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (property) {
      setFeatures({ ...(property.features || {}) });
      setLabel(property.client_label || 'Advertisers');
      setVisibility(property.rep_client_visibility || 'all');
      setDigest(!!property.sales_digest_enabled);
      setHour(Number.isInteger(property.sales_digest_hour) ? property.sales_digest_hour : 7);
      setTimezone(property.timezone || 'America/Edmonton');
      setMultiCurrency(property.multi_currency !== false);
      setError('');
    }
  }, [property]);

  if (!property) return null;

  const isOn = (key) => features[key] !== false;
  const setAll = (value) => setFeatures(Object.fromEntries(FEATURES.map(f => [f.key, value])));

  const save = async () => {
    if (!label.trim()) { setError('The list name cannot be empty.'); return; }
    setSaving(true);
    setError('');
    try {
      await api.put(`/properties/${property.id}`, {
        features: Object.fromEntries(FEATURES.map(f => [f.key, isOn(f.key)])),
        client_label: label.trim(),
        rep_client_visibility: visibility,
        sales_digest_enabled: digest,
        sales_digest_hour: hour,
        timezone,
        multi_currency: multiCurrency,
      });
      onSaved();
    } catch (e) {
      const d = e.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Could not save the settings.');
    } finally {
      setSaving(false);
    }
  };

  const selectCls = 'bg-[#0A0A0A] border-[#1E293B] text-[#F8FAFC]';
  const zones = TIMEZONES.includes(timezone) ? TIMEZONES : [timezone, ...TIMEZONES];

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#121212] border-[#1E293B] text-[#F8FAFC] max-w-xl max-h-[90vh] overflow-y-auto" data-testid="property-features-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><SlidersHorizontal className="w-5 h-5 text-[#3B82F6]" /> {property.name}: features</DialogTitle>
          <DialogDescription className="text-[#94A3B8]">
            A module that is off disappears from the menu for everyone in this property and is blocked on the server.
            Its data is kept and comes back when you turn it on again. Clients, Sales and Settings are always on.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] uppercase tracking-wide text-[#94A3B8]">Modules</p>
              <div className="flex gap-3 text-xs">
                <button type="button" onClick={() => setAll(true)} className="text-[#3B82F6]" data-testid="features-all-on">All on</button>
                <button type="button" onClick={() => setAll(false)} className="text-[#3B82F6]" data-testid="features-all-off">All off (sales team only)</button>
              </div>
            </div>
            <div className="border border-[#1E293B] rounded-md divide-y divide-[#1E293B]">
              {FEATURES.map(f => (
                <div key={f.key} className="flex items-center justify-between px-3 py-2.5">
                  <div>
                    <div className="text-sm text-[#F8FAFC]">{f.label}</div>
                    <div className="text-xs text-[#475569]">{f.hint}</div>
                  </div>
                  <Switch
                    checked={isOn(f.key)}
                    onCheckedChange={(v) => setFeatures(prev => ({ ...prev, [f.key]: v }))}
                    data-testid={`feature-switch-${f.key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Name of the client list</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} maxLength={30} placeholder="Advertisers" className={selectCls} data-testid="client-label-input" />
              <div className="flex gap-3 text-xs">
                {['Advertisers', 'Clients'].map(l => (
                  <button type="button" key={l} onClick={() => setLabel(l)} className="text-[#3B82F6]">{l}</button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">What sales reps see</Label>
              <p className="text-xs text-[#94A3B8]" data-testid="rep-visibility-note">
                Every client and who it is assigned to. In Sales they work only their own clients, and only
                admins and accounting can assign a client to a rep.
              </p>
            </div>
          </div>

          <div className="border border-[#1E293B] rounded-md p-3">
            <div className="flex items-center justify-between gap-4">
              <div>
                <div className="text-sm text-[#F8FAFC]">Multi-currency (USD + MXN)</div>
                <div className="text-xs text-[#475569]">
                  Invoices show the USD/MXN exchange rate and the MXN total, and clients get both PAY IN USD and PAY IN MXN buttons.
                  Everything is still recorded in USD. When off, the property is USD only: no exchange rates, no DOF lookups, no MXN receipts or facturas.
                </div>
              </div>
              <Switch checked={multiCurrency} onCheckedChange={setMultiCurrency} data-testid="multi-currency-switch" />
            </div>
          </div>

          <div className="border border-[#1E293B] rounded-md p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-[#F8FAFC]">Morning follow-up email</div>
                <div className="text-xs text-[#475569]">Monday to Friday, each rep gets their follow-ups due and overdue. Reps with nothing due get no email.</div>
              </div>
              <Switch checked={digest} onCheckedChange={setDigest} data-testid="digest-switch" />
            </div>
            {digest && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-[#94A3B8] text-xs">Send at</Label>
                  <Select value={String(hour)} onValueChange={(v) => setHour(parseInt(v, 10))}>
                    <SelectTrigger className={selectCls} data-testid="digest-hour-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#1E293B] max-h-[240px]">
                      {HOURS.map(h => <SelectItem key={h} value={String(h)} className="text-[#F8FAFC]">{hourLabel(h)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-[#94A3B8] text-xs">Time zone</Label>
                  <Select value={timezone} onValueChange={setTimezone}>
                    <SelectTrigger className={selectCls} data-testid="digest-timezone-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#121212] border-[#1E293B]">
                      {zones.map(z => <SelectItem key={z} value={z} className="text-[#F8FAFC]">{z}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <p className="col-span-2 text-xs text-[#475569]">Needs Email Settings filled in for this property, and an email on each Sales Rep record.</p>
              </div>
            )}
          </div>

          {error && <div className="text-sm text-red-400 bg-red-900/20 border border-red-800 rounded-md p-2" data-testid="features-error">{error}</div>}

          <div className="flex justify-end gap-3">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#1E293B] bg-[#121212] text-[#94A3B8]">Cancel</Button>
            <Button type="button" onClick={save} disabled={saving} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white" data-testid="save-features-btn">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PropertyFeaturesModal;
